import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Sparkles, Calendar, ArrowLeft, ChevronDown } from "lucide-react";
import { fetchPageBySlug, fetchReviews } from "../lib/api";
import ContactSection from "../components/ContactSection";
import ReviewsSlider from "../components/ReviewsSlider";
import NotFound from "./NotFound";

export default function DynamicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState(null); // All items closed by default

  const handleContentClick = (e) => {
    const target = e.target.closest("a");
    if (target && target.href) {
      try {
        const url = new URL(target.href);
        if (url.origin === window.location.origin) {
          // If it's just a hash on the same page, let standard behavior handle it
          if (url.pathname === window.location.pathname && url.hash) {
            return;
          }
          e.preventDefault();
          navigate(url.pathname + url.search + url.hash);
        }
      } catch (err) {
        console.error("Link navigation error:", err);
      }
    }
  };

  useEffect(() => {
    async function loadPageData() {
      setLoading(true);
      const pageData = await fetchPageBySlug(slug);
      setPage(pageData);

      if (slug === "referencer") {
        const reviewsData = await fetchReviews();
        setReviews(reviewsData);
      }
      setLoading(false);
      setOpenFaqIndex(null); // Reset accordion state (closed) when slug changes
    }
    loadPageData();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <span className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-neutral-500 text-sm">Henter indhold...</p>
      </div>
    );
  }

  if (!page) {
    return <NotFound />;
  }

  // Helper function to link the very first mention of the company name "John Calmar" in body text to the front page
  function linkFirstCompanyMention(htmlString) {
    if (!htmlString || !htmlString.includes("John Calmar")) return htmlString;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");

      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          let parent = node.parentElement;
          while (parent && parent !== doc.body) {
            const tag = parent.tagName.toLowerCase();
            if (["h1", "h2", "h3", "h4", "h5", "h6", "a", "button", "script", "style"].includes(tag)) {
              return NodeFilter.FILTER_REJECT;
            }
            parent = parent.parentElement;
          }
          if (node.nodeValue && /John\s+Calmar/i.test(node.nodeValue)) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        },
      });

      const targetNode = walker.nextNode();
      if (targetNode) {
        const text = targetNode.nodeValue;
        const match = text.match(/John\s+Calmar(['`’]s|s)?/i);
        if (match) {
          const matchIndex = match.index;
          const matchedStr = match[0];
          const suffix = match[1] || "";
          const before = text.slice(0, matchIndex);
          const after = text.slice(matchIndex + matchedStr.length);

          const frag = doc.createDocumentFragment();
          if (before) frag.appendChild(doc.createTextNode(before));

          const link = doc.createElement("a");
          link.href = "/";
          link.className =
            "text-neutral-900 font-medium hover:text-brand-600 underline underline-offset-4 decoration-brand-400/60 hover:decoration-brand-600 transition-colors";
          link.textContent = "John Calmar";
          frag.appendChild(link);

          if (suffix) {
            frag.appendChild(doc.createTextNode(suffix));
          }
          if (after) {
            frag.appendChild(doc.createTextNode(after));
          }

          targetNode.parentNode.replaceChild(frag, targetNode);
        }
      }

      return doc.body.innerHTML;
    } catch (err) {
      console.error("Error linking first company mention:", err);
      return htmlString;
    }
  }

  // Helper function to parse flat HTML content into an intro and structured FAQ items
  function parseContentToFaq(htmlString) {
    if (!htmlString) return { introHtml: "", faqItems: [] };

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const children = Array.from(doc.body.children);

    let introHtml = "";
    const faqItems = [];
    let currentFaq = null;
    let skippedH1 = false;

    for (const child of children) {
      const tagName = child.tagName.toLowerCase();

      // Skip the first h1 since we already display the title in the hero
      if (tagName === "h1") {
        if (!skippedH1) {
          skippedH1 = true;
          continue;
        }
      }

      // Check if this is a heading that should start a new FAQ item
      const isHeading = ["h2", "h3", "h4"].includes(tagName);

      if (isHeading) {
        if (currentFaq) {
          faqItems.push(currentFaq);
        }
        currentFaq = {
          question: child.textContent.trim(),
          contentHtml: ""
        };
      } else {
        if (currentFaq) {
          currentFaq.contentHtml += child.outerHTML;
        } else {
          introHtml += child.outerHTML;
        }
      }
    }

    if (currentFaq) {
      faqItems.push(currentFaq);
    }

    return { introHtml, faqItems };
  }

  // Helper function to get page specific image or default fallback
  const getPageImage = () => {
    // 1. If page has an og_image defined in CMS, use it
    if (page?.og_image && page.og_image.length > 0 && page.og_image[0]) {
      return page.og_image[0];
    }

    // 2. Specific per-slug custom image mapping so pages don't share the exact same hardcoded image
    const slugImageMap = {
      "om-john": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-om-historie-portraet.jpg",
      "referencer": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-klient-referencer-session.jpg",
      "auratydning": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/auratydning-aura-analyse-energifelt.jpg",
      "kurser-og-uddannelse": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-kursus-uddannelse-session.jpg",
      "shamanisme": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/shamanisme-shamanistiske-kurser.jpg",
      "klarsyn-online": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmars-univers-klarsyn.jpg",
      "klarsynsaften-online": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-klarsynsaften-online.jpg",
      "calmars-univers": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmars-univers-medlemskab.jpg",
      "parterapi": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-parterapi-aarhus.jpg",
      "hypnose": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/hypnose-afslapning-og-forandring.jpg",
      "kontakt": "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-spirituel-radgivning-kontakt.jpg",
    };

    if (slug && slugImageMap[slug]) {
      return slugImageMap[slug];
    }

    // Default fallback
    return "https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-spirituel-radgivning-kontakt.jpg";
  };

  const getPageImageAlt = () => {
    const altMap = {
      "om-john": "Portræt af John Calmar – Erfaren clairvoyant, shaman og hypnoterapeut i Viby J ved Aarhus",
      "referencer": "John Calmar i personlig session med klient – Anbefalinger og referencer for clairvoyance og hypnose",
      "auratydning": "Aura-analyse, energifelter og chakratydning med aurakamera hos John Calmar i Viby J",
      "kurser-og-uddannelse": "Kurser og uddannelse i clairvoyance, intuition og shamanisme med John Calmar",
      "kurser-workshop": "Kurser og workshops i auratydning og meditation hos John Calmar",
      "shamanisme": "Shamanisme, naturvisdom, kraftdyr og trommerejser med shaman John Calmar",
      "klarsyn-online": "John Calmars Univers – Ugentligt online klarsyn og guidet meditation",
      "klarsynsaften-online": "Online klarsynsaften den første onsdag i hver måned med John Calmar",
      "calmars-univers": "Bliv medlem af John Calmars Univers for personlige klarsyn og spirituelt fællesskab",
      "parterapi": "Parterapi og relationssamtale i rolige rammer hos John Calmar i Viby J ved Aarhus",
      "moed-mig": "Mød John Calmar på spirituelle messer og Krop-Sind-Ånd arrangementer i Danmark",
      "kontakt": "Kontakt John Calmar – Spirituel rådgivning i Viby J ved Aarhus samt online",
    };
    if (slug && altMap[slug]) return altMap[slug];
    return `${page?.title || "John Calmar"} – Clairvoyance & Hypnose i Aarhus`;
  };

  const rawProcessedContent = linkFirstCompanyMention(page.content);
  const currentImage = getPageImage();
  // Ensure all images in content have descriptive alt text and lazy loading
  const processedContent = (() => {
    if (!rawProcessedContent || !rawProcessedContent.includes("<img")) return rawProcessedContent;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawProcessedContent, "text/html");
      const images = doc.querySelectorAll("img");
      images.forEach((img, idx) => {
        if (!img.getAttribute("alt") || img.getAttribute("alt").trim() === "") {
          img.setAttribute("alt", `${page.title || "John Calmar"} – billede ${idx + 1}`);
        }
        img.setAttribute("loading", "lazy");
      });
      return doc.body.innerHTML;
    } catch {
      return rawProcessedContent;
    }
  })();
  const { introHtml, faqItems } = parseContentToFaq(processedContent);

  // Extract clean title without " | JohnCalmar.dk" for the hero display
  const displayTitle = page.title.replace(" | JohnCalmar.dk", "").replace(" | John Calmar", "");

  return (
    <>
      <Helmet>
        <title>{page.title ? (page.title.includes("John Calmar") ? page.title : `${page.title} | John Calmar`) : "John Calmar | Clairvoyance & Hypnose"}</title>
        {page.meta_description && <meta name="description" content={page.meta_description} />}
        <link rel="canonical" href={`https://johncalmar.dk/${slug}`} />
        <meta property="og:title" content={page.og_title || page.title} />
        {page.og_description && <meta property="og:description" content={page.og_description} />}
        {page.og_image && page.og_image.length > 0 && (
          <meta property="og:image" content={page.og_image[0]} />
        )}
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.og_title || page.title} />
        {page.og_description && <meta name="twitter:description" content={page.og_description} />}
        {page.og_image && page.og_image.length > 0 && (
          <meta name="twitter:image" content={page.og_image[0]} />
        )}
      </Helmet>

      {/* Main Content Section */}
      <section className="py-16 md:py-24 bg-white" onClick={handleContentClick}>
        <div className="max-w-[1400px] mx-auto px-6">
          {faqItems.length > 0 ? (
            <div className={slug === "klarsyn-online" ? "max-w-4xl mx-auto space-y-8" : "grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start"}>
              {/* Left Column: Sticky Header & Intro */}
              {slug !== "klarsyn-online" && (
                <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-28">
                  {/* Billede øverst / til venstre */}
                  <div>
                    <div className="overflow-hidden rounded-2xl shadow-lg border border-neutral-200/60 bg-white">
                      <img
                        src={currentImage}
                        alt={getPageImageAlt()}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Right Column: FAQ Accordion */}
              <div className={slug === "klarsyn-online" ? "text-left space-y-8" : "lg:col-span-7 text-left space-y-8"}>
                <div className="space-y-4 text-left">
                  <h2 className="font-sans font-bold text-2xl sm:text-3xl md:text-4xl text-neutral-950 leading-tight uppercase tracking-wide break-words">
                    {displayTitle}
                  </h2>
                  {introHtml ? (
                    <div
                      className="prose-custom text-neutral-600 text-base font-light leading-relaxed space-y-4"
                      dangerouslySetInnerHTML={{ __html: introHtml }}
                    />
                  ) : (
                    <p className="text-neutral-600 text-base font-light leading-relaxed">
                      {displayTitle.toLowerCase().startsWith("om ") ? (
                        <>Læs mere om {displayTitle.substring(3)} herunder, hvor jeg har samlet de vigtigste informationer og svar på dine spørgsmål.</>
                      ) : (
                        <>Læs mere om {displayTitle.toLowerCase()} herunder, hvor jeg har samlet de vigtigste informationer og svar på dine spørgsmål.</>
                      )}
                    </p>
                  )}
                </div>

                <div className="border-t border-neutral-200/60">
                {faqItems.map((item, idx) => {
                  const isOpen = openFaqIndex === idx;
                  return (
                    <div key={idx} className="border-b border-neutral-200/60">
                      <button
                        onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                        className="w-full py-5 flex justify-between items-center text-left font-sans text-lg md:text-xl font-semibold text-neutral-950 hover:text-brand-600 transition-colors duration-200 group focus:outline-none uppercase tracking-wide"
                      >
                        <span>{item.question}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-neutral-400 group-hover:text-brand-600 transition-transform duration-300 shrink-0 ${
                            isOpen ? "transform rotate-180 text-brand-600" : ""
                          }`}
                        />
                      </button>
                      <div
                        className={`grid transition-all duration-300 ease-in-out ${
                          isOpen ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0 overflow-hidden"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div
                            className="pt-2 text-neutral-600 leading-relaxed text-base space-y-4 prose-custom"
                            dangerouslySetInnerHTML={{ __html: item.contentHtml }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>

                {/* CTA Button */}
                <div className="pt-2 text-left">
                  <a
                    href="#kontakt"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white hover:text-white font-semibold rounded-xl text-sm transition-colors shadow-lg shadow-brand-600/10"
                  >
                    Bestil tid / Kontakt mig
                  </a>
                </div>
              </div>
            </div>
          ) : slug === "referencer" ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Left Column: Image */}
              <div className="lg:col-span-6 order-2 lg:order-1">
                <div className="overflow-hidden rounded-2xl shadow-lg border border-neutral-200/60 bg-white">
                  <img
                    src={currentImage}
                    alt={getPageImageAlt()}
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Right Column: Intro, Title & Button */}
              <div className="lg:col-span-6 order-1 lg:order-2 space-y-6 text-left flex flex-col justify-center">
                <div className="space-y-4">
                  <h1 className="font-sans font-bold text-2xl sm:text-3xl md:text-4xl text-neutral-950 leading-tight uppercase tracking-wide break-words">
                    {displayTitle}
                  </h1>
                  <div
                    className="prose-custom text-neutral-700 text-base sm:text-lg font-light leading-relaxed space-y-4"
                    dangerouslySetInnerHTML={{ __html: processedContent }}
                  />
                </div>

                <div className="pt-2">
                  <a
                    href="#kontakt"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white hover:text-white font-semibold rounded-xl text-sm transition-colors shadow-lg shadow-brand-600/10"
                  >
                    Bestil tid / Kontakt mig
                  </a>
                </div>
              </div>

              {/* Full-width Reviews Slider */}
              <div className="lg:col-span-12 order-3 pt-4">
                {reviews.length > 0 && (
                  <ReviewsSlider reviews={reviews} dark={false} />
                )}
              </div>
            </div>
          ) : slug === "moed-mig" ? (
            <div className="max-w-5xl xl:max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start justify-center">
              {/* Left Column (Desktop): Om John video fremhævet i et elegant kort */}
              <div className="order-2 lg:order-1 lg:col-span-5 lg:sticky lg:top-28 flex flex-col items-center lg:items-center">
                <div className="w-full max-w-[340px] sm:max-w-[360px] bg-white rounded-3xl p-3 sm:p-4 shadow-xl border border-neutral-200/90 transition-all">
                  <div className="flex items-center justify-between pb-3 px-1 text-xs text-neutral-500 font-medium">
                    <span className="inline-flex items-center gap-1.5 text-neutral-800 font-semibold tracking-wide">
                      <i className="fa-solid fa-circle-play text-neutral-900" aria-hidden="true" />
                      Hør mere her
                    </span>
                    <span className="text-[11px] text-neutral-400 uppercase tracking-wider">Præsentation</span>
                  </div>

                  <div className="w-full aspect-[9/16] rounded-2xl overflow-hidden bg-neutral-950 shadow-inner">
                    <video
                      controls
                      playsInline
                      preload="auto"
                      src="/_assets/om-john-video.mov#t=2"
                      poster="/_assets/john-calmar-clairvoyant-shaman-portraet.jpeg"
                      title="Præsentationsvideo med John Calmar om clairvoyance og spirituelt arbejde"
                      aria-label="Video med John Calmar"
                      onLoadedMetadata={(e) => {
                        if (e.currentTarget.currentTime < 2) {
                          e.currentTarget.currentTime = 2;
                        }
                      }}
                      className="w-full h-full object-cover"
                    >
                      Din browser understøtter desværre ikke videoafspilning.
                    </video>
                  </div>
                  <p className="mt-3 text-center text-xs text-neutral-500 font-light">
                    Hør John fortælle om sit arbejde og mødet med mennesker.
                  </p>
                </div>
              </div>

              {/* Right Column (Desktop): Heading, text and structured links */}
              <div className="order-1 lg:order-2 lg:col-span-7 space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-widest font-semibold text-neutral-500 mb-2 block">
                    Arrangementer & Messer
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-neutral-950 leading-tight uppercase tracking-wide text-left break-words">
                    {displayTitle}
                  </h1>
                </div>

                {/* Content intro */}
                <div
                  className="prose-custom text-neutral-700 text-base sm:text-lg font-light leading-relaxed space-y-4 text-left"
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                />

                {/* Direkte kontakt / session CTA-kort */}
                <div className="pt-2">
                  <div className="p-6 rounded-2xl bg-neutral-50 border border-neutral-200/80 space-y-3">
                    <h3 className="text-lg font-medium text-neutral-900 flex items-center gap-2">
                      <i className="fa-regular fa-calendar-check text-neutral-700" aria-hidden="true" />
                      Personlig session med John
                    </h3>
                    <p className="text-neutral-600 text-sm font-light leading-relaxed">
                      Ønsker du en individuel session eller har du spørgsmål til et arrangement, er du altid velkommen til at kontakte mig direkte.
                    </p>
                    <div className="pt-2 flex flex-wrap gap-3">
                      <a
                        href="/kontakt"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-neutral-900 text-white text-xs uppercase tracking-wider font-medium hover:bg-neutral-800 transition-colors shadow-sm"
                      >
                        <i className="fa-regular fa-envelope" aria-hidden="true" />
                        Kontakt mig
                      </a>
                      <a
                        href="tel:40938593"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-neutral-900 text-xs uppercase tracking-wider font-medium border border-neutral-300 hover:bg-neutral-100 transition-colors"
                      >
                        <i className="fa-solid fa-phone" aria-hidden="true" />
                        40 93 85 93
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl space-y-8">
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-light text-neutral-950 leading-tight uppercase tracking-wide text-left break-words">
                {displayTitle}
              </h1>
              {/* Rich Text Content with Premium Typography (Fallback) */}
              <div
                className="prose-custom text-neutral-700 text-base sm:text-lg font-light leading-relaxed space-y-6 text-left"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Relaterede interne links sektion for forbedret browsing og SEO */}
      <section className="bg-neutral-50/70 border-t border-neutral-200/60 py-16">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-700">
              Udforsk mere
            </span>
            <h3 className="text-2xl sm:text-3xl font-light text-neutral-950 uppercase tracking-wide mt-2">
              Flere tilbud hos John Calmar
            </h3>
            <p className="text-neutral-600 text-sm font-light mt-2">
              Læs om mine øvrige ydelser, sessioner og kurser i Viby J ved Aarhus og online.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            <a
              href="/clairvoyance-online"
              className="group p-4 bg-white border border-neutral-200/80 hover:border-brand-600/50 rounded-xl text-center transition-all duration-200 shadow-2xs hover:shadow-sm"
            >
              <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center mx-auto mb-2.5 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                <i className="fa-sharp fa-light fa-sparkles text-sm" aria-hidden="true" />
              </div>
              <span className="text-xs font-semibold text-neutral-900 group-hover:text-brand-700 block uppercase tracking-wider">
                Clairvoyance
              </span>
              <span className="text-[11px] text-neutral-500 font-light block mt-0.5">
                Klarhed & indsigt
              </span>
            </a>

            <a
              href="/hypnose"
              className="group p-4 bg-white border border-neutral-200/80 hover:border-brand-600/50 rounded-xl text-center transition-all duration-200 shadow-2xs hover:shadow-sm"
            >
              <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center mx-auto mb-2.5 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                <i className="fa-sharp fa-light fa-brain text-sm" aria-hidden="true" />
              </div>
              <span className="text-xs font-semibold text-neutral-900 group-hover:text-brand-700 block uppercase tracking-wider">
                Hypnose
              </span>
              <span className="text-[11px] text-neutral-500 font-light block mt-0.5">
                Bryd vaner & mønstre
              </span>
            </a>

            <a
              href="/shamanisme"
              className="group p-4 bg-white border border-neutral-200/80 hover:border-brand-600/50 rounded-xl text-center transition-all duration-200 shadow-2xs hover:shadow-sm"
            >
              <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center mx-auto mb-2.5 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                <i className="fa-sharp fa-light fa-feather text-sm" aria-hidden="true" />
              </div>
              <span className="text-xs font-semibold text-neutral-900 group-hover:text-brand-700 block uppercase tracking-wider">
                Shamanisme
              </span>
              <span className="text-[11px] text-neutral-500 font-light block mt-0.5">
                Trommerejser & visdom
              </span>
            </a>

            <a
              href="/auratydning"
              className="group p-4 bg-white border border-neutral-200/80 hover:border-brand-600/50 rounded-xl text-center transition-all duration-200 shadow-2xs hover:shadow-sm"
            >
              <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center mx-auto mb-2.5 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                <i className="fa-sharp fa-light fa-eye text-sm" aria-hidden="true" />
              </div>
              <span className="text-xs font-semibold text-neutral-900 group-hover:text-brand-700 block uppercase tracking-wider">
                Auratydning
              </span>
              <span className="text-[11px] text-neutral-500 font-light block mt-0.5">
                Chakra & energifelt
              </span>
            </a>

            <a
              href="/calmars-univers"
              className="group p-4 bg-white border border-neutral-200/80 hover:border-brand-600/50 rounded-xl text-center transition-all duration-200 shadow-2xs hover:shadow-sm"
            >
              <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center mx-auto mb-2.5 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                <i className="fa-sharp fa-light fa-moon-stars text-sm" aria-hidden="true" />
              </div>
              <span className="text-xs font-semibold text-neutral-900 group-hover:text-brand-700 block uppercase tracking-wider">
                Calmars Univers
              </span>
              <span className="text-[11px] text-neutral-500 font-light block mt-0.5">
                Klarsyn & meditation
              </span>
            </a>

            <a
              href="/butik"
              className="group p-4 bg-white border border-neutral-200/80 hover:border-brand-600/50 rounded-xl text-center transition-all duration-200 shadow-2xs hover:shadow-sm"
            >
              <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center mx-auto mb-2.5 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                <i className="fa-sharp fa-light fa-bag-shopping text-sm" aria-hidden="true" />
              </div>
              <span className="text-xs font-semibold text-neutral-900 group-hover:text-brand-700 block uppercase tracking-wider">
                Online Butik
              </span>
              <span className="text-[11px] text-neutral-500 font-light block mt-0.5">
                Book & køb sessioner
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />
    </>
  );
}
